import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { route } from '@aurelia/router';
import template from './app-updates-view.html';
import { ConfirmDialog } from '../components/confirm-dialog';
import {
  deleteAppUpdate,
  formatFileSize,
  listAppUpdates,
  setAppUpdateChannels,
  uploadAppUpdate,
} from '../services/api.service';
import type { AppUpdateRelease } from '../services/api.types';
import { hasPermission } from '../services/permissions.service';

@route({ path: 'app-updates', title: 'App updates' })
@customElement({ name: 'app-updates-view', template })
export class AppUpdatesView {
  private readonly i18n: I18N = resolve(I18N);

  public canLoad(): boolean | string {
    return hasPermission('maintenance.access') ? true : 'gallery';
  }

  public releases: AppUpdateRelease[] = [];
  public selectedReleaseId = '';
  public currentReleaseId = '';
  public previewReleaseId = '';
  public showUploadDialog = false;
  public selectedFile: File | null = null;
  public loadingReleases = false;
  public savingChannels = false;
  public uploading = false;
  public errorMessage = '';
  public uploadErrorMessage = '';
  public fileInput: HTMLInputElement | null = null;

  public async loading(): Promise<void> {
    await this.refresh();
  }

  public async refresh(): Promise<void> {
    this.loadingReleases = true;
    this.errorMessage = '';
    try {
      this.releases = await listAppUpdates();
      this.currentReleaseId = this.releases.find(release => release.isCurrent)?.id ?? '';
      this.previewReleaseId = this.releases.find(release => release.isPreview)?.id ?? '';
      if (this.selectedReleaseId && !this.releases.some(release => release.id === this.selectedReleaseId))
        this.selectedReleaseId = '';
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.loadingReleases = false;
    }
  }

  public selectRelease(release: AppUpdateRelease): void {
    this.selectedReleaseId = release.id;
  }

  public openUploadDialog(): void {
    this.selectedFile = null;
    this.uploadErrorMessage = '';
    this.showUploadDialog = true;
  }

  public closeUploadDialog(): void {
    if (this.uploading) return;
    this.showUploadDialog = false;
    this.selectedFile = null;
    this.uploadErrorMessage = '';
    if (this.fileInput) this.fileInput.value = '';
  }

  public selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    this.uploadErrorMessage = '';
  }

  public async upload(): Promise<void> {
    if (this.uploading) return;
    if (!this.selectedFile) {
      this.uploadErrorMessage = this.i18n.tr('appUpdates.fileRequired');
      return;
    }

    this.uploading = true;
    this.uploadErrorMessage = '';
    try {
      const uploadedRelease = await uploadAppUpdate(this.selectedFile);
      this.selectedFile = null;
      if (this.fileInput) this.fileInput.value = '';
      this.showUploadDialog = false;
      await this.refresh();
      this.selectedReleaseId = uploadedRelease.id;
    } catch (error) {
      this.uploadErrorMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.uploading = false;
    }
  }

  public async setCurrent(): Promise<void> {
    if (!this.selectedReleaseId || this.selectedReleaseId === this.currentReleaseId)
      return;
    const previewReleaseId = this.selectedReleaseId === this.previewReleaseId
      ? null
      : this.previewReleaseId;
    await this.applyChannels(this.selectedReleaseId, previewReleaseId);
  }

  public async setPreview(): Promise<void> {
    if (!this.selectedReleaseId || this.selectedReleaseId === this.previewReleaseId)
      return;
    const currentReleaseId = this.selectedReleaseId === this.currentReleaseId
      ? null
      : this.currentReleaseId;
    await this.applyChannels(currentReleaseId, this.selectedReleaseId);
  }

  public async clearCurrent(): Promise<void> {
    if (!this.currentReleaseId) return;
    await this.applyChannels(null, this.previewReleaseId);
  }

  public async clearPreview(): Promise<void> {
    if (!this.previewReleaseId) return;
    await this.applyChannels(this.currentReleaseId, null);
  }

  public async deleteSelected(): Promise<void> {
    const release = this.releases.find(candidate => candidate.id === this.selectedReleaseId);
    if (release) await this.deleteRelease(release);
  }

  private async applyChannels(currentReleaseId: string | null, previewReleaseId: string | null): Promise<void> {
    if (this.savingChannels) return;

    this.savingChannels = true;
    this.errorMessage = '';
    try {
      this.releases = await setAppUpdateChannels(currentReleaseId, previewReleaseId);
      this.currentReleaseId = this.releases.find(release => release.isCurrent)?.id ?? '';
      this.previewReleaseId = this.releases.find(release => release.isPreview)?.id ?? '';
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.savingChannels = false;
    }
  }

  public async deleteRelease(release: AppUpdateRelease): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('appUpdates.deleteConfirm', { version: release.displayVersion }),
      this.i18n.tr('appUpdates.deleteTitle'),
    );
    if (!confirmed) return;

    try {
      await deleteAppUpdate(release.id);
      await this.refresh();
      if (this.selectedReleaseId === release.id)
        this.selectedReleaseId = '';
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('common.error'),
      );
    }
  }

  public formatBytes(bytes: number): string {
    return formatFileSize(bytes);
  }

  public formatDate(value: string): string {
    return new Date(value).toLocaleString();
  }
}
